import { useEffect, useRef } from 'react'
import type { MapboxCustomLayerService } from '@/services/3d'
import { ThrottledModelQueue } from '@/services/3d'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { eventItemToPlaced } from '../mappers/eventItemToPlaced'
import { computeActorPose, type ActorPose, type MovementPlan } from '../motion/movementPlan'
import { isEventActorId } from '../motion/buildMovementPlan'
import { ProceduralTugFactory } from '../services/ProceduralTugFactory'
import { ProceduralFuelTruckFactory } from '../services/ProceduralFuelTruckFactory'
import type { FuelRequest, FuelType } from '../motion/fuelRequest'

/**
 * Reconcile the set of VISIBLE actors (aircraft + tugs + fuel trucks) against
 * the Three.js scene — MEMBERSHIP only. The per-frame transform is applied by
 * `useEventActorMotion`.
 *
 * An actor is "present" when its plan yields a pose at the current clock time
 * (`computeActorPose != null`), which for an aircraft spans the whole plan
 * (approach → parked → exit) and for a tug only its tow sub-windows. Diff
 * signatures are IDENTITY ONLY (model typeId / 'tug' / fuel type) so a moving
 * actor is never removed/re-added mid-motion.
 *
 * ## Membership is visibility, not construction
 *
 * Building an actor is expensive (a GLTF clone with deep-cloned geometry and
 * materials) and destroying it frees GPU buffers that the very next scrub may
 * ask for again. So an actor that leaves the stage is only HIDDEN
 * (`setSceneObjectVisible(id, false)`) and stays resident: re-entry — the common
 * case when scrubbing across an arrival boundary — is then a flag flip.
 *
 * That pool is bounded. With a 400–600 aircraft fleet against ~117 concurrent
 * slots, retaining everything ever shown would eventually hold the whole fleet
 * in GPU memory, so hidden residents beyond `MAX_RETAINED_HIDDEN` are disposed
 * least-recently-desired first. Desired actors are never evicted.
 *
 * ## Nothing stays on stage that doesn't belong
 *
 * Three mechanisms, because each covers a hole the others can't:
 *  - the per-pass diff hides/evicts actors this hook knows about;
 *  - an ORPHAN SWEEP removes any `evt:`/`tug:`/`fuel:` object in the scene that
 *    this hook is *not* tracking — the catch-all for an add that landed after
 *    its cancellation, or objects removed behind our back by another effect;
 *  - an EPOCH GUARD re-removes a model whose (async) load resolved after the
 *    actor was cancelled or the stage was purged.
 * Starting playback empties the retained pool, and `resetStage()` (the store's
 * stage epoch) tears the whole stage down and rebuilds it at the current time.
 *
 * Adds are funneled through a cache-aware ThrottledModelQueue: a burst of new
 * actors (e.g. a high-speed arrival rush) is paced at one insert per 100ms when
 * the model is already cached, 500ms when it needs a network load — so the
 * renderer never hitches instantiating dozens at once. Because aircraft adds
 * recompute pose(t) at drain time, a late-drained actor still lands at the
 * right place.
 *
 * Cadence: membership changes only at coarse plan-interval boundaries (legs and
 * dwells span tens of seconds to minutes), so this heavy O(placements × actors)
 * reconcile is gated to a quantized sim clock instead of running every animation
 * frame. The per-frame transform pass (`useEventActorMotion`) stays at full rate.
 * Trade-off: at extreme playback speeds an actor may appear/disappear up to one
 * quantum late — invisible at normal review speeds, and the gate naturally fires
 * more often as the sim clock advances faster.
 */
const RECONCILE_QUANTUM_MS = 500

/**
 * How many off-stage models stay resident (hidden) for instant re-entry.
 * Resident total settles at roughly peak-concurrent (~150 actors incl. tugs and
 * trucks) plus this pool — bounded regardless of fleet size.
 */
const MAX_RETAINED_HIDDEN = 80

export function useEventSceneSync(
  layerService: MapboxCustomLayerService | null,
  plans: Record<string, MovementPlan>,
  fuelByScheduleId?: Record<string, FuelRequest>
) {
  const placements = useEventPlannerStore((s) => s.placements)
  // Subscribe to a quantized sim clock, not raw currentTime: re-render/reconcile
  // only when the bucket advances (~2×/sec at 1× playback) instead of every frame.
  const reconcileTick = useEventPlannerStore((s) => Math.floor(s.currentTime / RECONCILE_QUANTUM_MS))
  const catalog = useEventPlannerStore((s) => s.catalog)
  const motionConfig = useEventPlannerStore((s) => s.motionConfig)
  const playback = useEventPlannerStore((s) => s.playback)
  const stageEpoch = useEventPlannerStore((s) => s.stageEpoch)

  // Objects currently IN the scene → their identity signature.
  const residentSig = useRef(new Map<string, string>())
  // Subset of residents currently shown.
  const visibleIds = useRef(new Set<string>())
  // Monotonic recency stamp per actor (LRU key for pool eviction). A counter,
  // not the sim clock, so scrubbing backwards doesn't invert the ordering.
  const lastDesiredSeq = useRef(new Map<string, number>())
  const seq = useRef(0)
  // Queued-but-not-yet-realized adds → the signature they were queued for.
  const pendingSig = useRef(new Map<string, string>())
  // Last parked pose applied to an in-scene actor (animation-off path only), so a
  // live zone-settings edit repositions already-placed planes without re-adding.
  const parkedSig = useRef(new Map<string, string>())
  // Bumped on every hard purge; a queued add that resolves against a stale epoch
  // undoes itself instead of resurrecting a cleared stage.
  const epoch = useRef(0)
  const prevPlayback = useRef(playback)
  const prevStageEpoch = useRef(stageEpoch)
  const queueRef = useRef<ThrottledModelQueue | null>(null)
  if (!queueRef.current) queueRef.current = new ThrottledModelQueue()

  useEffect(() => {
    if (!layerService) return
    // Read the precise clock once per reconcile (the subscription is quantized).
    const currentTime = useEventPlannerStore.getState().currentTime
    const queue = queueRef.current!
    const animate = motionConfig.enableAnimation

    /** Forget an actor entirely and take its model out of the scene. */
    const dispose = (id: string) => {
      layerService.removeAircraft(id)
      residentSig.current.delete(id)
      visibleIds.current.delete(id)
      lastDesiredSeq.current.delete(id)
      parkedSig.current.delete(id)
    }

    // Reset Stage: tear the whole stage down, then let the pass below rebuild
    // exactly the actors present at the current clock time.
    if (prevStageEpoch.current !== stageEpoch) {
      prevStageEpoch.current = stageEpoch
      epoch.current++
      queue.clear()
      layerService.removeSceneObjectsWhere(isEventActorId)
      residentSig.current.clear()
      visibleIds.current.clear()
      lastDesiredSeq.current.clear()
      pendingSig.current.clear()
      parkedSig.current.clear()
    }

    const startedPlaying = prevPlayback.current !== 'playing' && playback === 'playing'
    prevPlayback.current = playback

    // Throttle is OFF by default (delays 0 ⇒ instant inserts). When enabled,
    // pace both the membership-add queue and the service's zoom LOD reload queue.
    const thr = motionConfig.throttleModelInsertion
    const cd = thr ? motionConfig.modelInsertCachedDelayMs : 0
    const ld = thr ? motionConfig.modelInsertLoadDelayMs : 0
    queue.setDelays(cd, ld)
    layerService.setModelInsertThrottle(cd, ld)
    const loader = layerService.getAircraftLoader()

    /**
     * Queue an insert for `id`, claiming it as pending until it either lands or
     * provably didn't. `insert` reports whether an object actually entered the
     * scene and whether it had to hit the network (the queue's pacing signal).
     */
    const enqueueAdd = (
      id: string,
      sig: string,
      insert: () =>
        | Promise<{ added: boolean; neededLoad: boolean }>
        | { added: boolean; neededLoad: boolean }
    ) => {
      const atEpoch = epoch.current
      queue.enqueue(id, async () => {
        // Cancelled, or superseded by a different identity, before we got a slot.
        if (pendingSig.current.get(id) !== sig) return { neededLoad: false }
        try {
          const { added, neededLoad } = await insert()
          if (added) {
            if (epoch.current !== atEpoch || pendingSig.current.get(id) !== sig) {
              // Cancelled or purged while the (async) load was in flight — the
              // model still landed, so take it straight back out rather than
              // leave it orphaned on stage.
              layerService.removeAircraft(id)
            } else {
              pendingSig.current.delete(id)
              residentSig.current.set(id, sig)
              visibleIds.current.add(id)
            }
          }
          return { neededLoad }
        } finally {
          // Nothing adopted the claim (build failed, threw, or went stale) —
          // release it so the next reconcile retries instead of waiting forever.
          if (pendingSig.current.get(id) === sig) pendingSig.current.delete(id)
        }
      })
    }

    // Desired visible set across all actors (identity signature + enqueue fn).
    const desired = new Map<string, { sig: string; pose: ActorPose | null; enqueue: () => void }>()

    for (const p of Object.values(placements)) {
      const plan = plans[p.eventAllocationId]
      const entry = catalog.get(p.typeCode)
      if (!entry) continue

      if (!animate || !plan) {
        // Legacy: aircraft only, [arrival, departure], parked pose.
        if (!(p.arrival <= currentTime && currentTime < p.departure)) continue
        const id = `evt:${p.eventAllocationId}`
        const sig = entry.typeId
        desired.set(id, {
          sig,
          pose: null,
          enqueue: () =>
            enqueueAdd(id, sig, async () => {
              const placed = eventItemToPlaced(p, catalog)
              if (!placed) return { added: false, neededLoad: false }
              const neededLoad = loader?.needsNetworkLoad(placed) ?? false
              const obj = await layerService.addAircraft(placed, { externalOwner: true })
              return { added: !!obj, neededLoad }
            }),
        })
        continue
      }

      for (const actor of plan.actors) {
        const pose0 = computeActorPose(plan, actor.id, currentTime)
        if (!pose0) continue // not present at t
        const id = actor.id

        if (actor.kind === 'aircraft') {
          const sig = entry.typeId
          desired.set(id, {
            sig,
            pose: pose0,
            enqueue: () =>
              enqueueAdd(id, sig, async () => {
                // Recompute pose at drain time so a late-drained add lands at
                // the current clock position, not the stale enqueue-time pose.
                const t = useEventPlannerStore.getState().currentTime
                const pose = computeActorPose(plan, id, t) ?? pose0
                const placed = eventItemToPlaced(p, catalog, {
                  position: pose.position,
                  rotationDeg: pose.rotationDeg,
                })
                if (!placed) return { added: false, neededLoad: false }
                const neededLoad = loader?.needsNetworkLoad(placed) ?? false
                const obj = await layerService.addAircraft(placed, { externalOwner: true })
                return { added: !!obj, neededLoad }
              }),
          })
        } else if (actor.kind === 'fuel_truck') {
          const fuelType: FuelType = fuelByScheduleId?.[p.scheduleId]?.fuelType ?? 'jet_a'
          const sig = `fuel:${fuelType}`
          desired.set(id, {
            sig,
            pose: pose0,
            enqueue: () =>
              enqueueAdd(id, sig, () => {
                const truck = ProceduralFuelTruckFactory.create(fuelType)
                const added = layerService.addSceneObject(truck, id)
                if (added) {
                  const t = useEventPlannerStore.getState().currentTime
                  const pose = computeActorPose(plan, id, t)
                  if (pose) layerService.setAircraftTransform(id, pose.position, pose.rotationDeg)
                }
                return { added, neededLoad: false } // procedural = instant
              }),
          })
        } else {
          const sig = 'tug'
          desired.set(id, {
            sig,
            pose: pose0,
            enqueue: () =>
              enqueueAdd(id, sig, () => {
                const tug = ProceduralTugFactory.create()
                const added = layerService.addSceneObject(tug, id)
                if (added) {
                  const t = useEventPlannerStore.getState().currentTime
                  const pose = computeActorPose(plan, id, t)
                  if (pose) layerService.setAircraftTransform(id, pose.position, pose.rotationDeg)
                }
                return { added, neededLoad: false } // procedural = instant
              }),
          })
        }
      }
    }

    // 1. Off-stage residents: hide, keep resident (retained pool).
    for (const id of residentSig.current.keys()) {
      if (desired.has(id)) continue
      if (visibleIds.current.has(id)) {
        layerService.setSceneObjectVisible(id, false)
        visibleIds.current.delete(id)
      }
    }

    // 2. Queued adds that are no longer wanted: drop them. Anything already
    //    mid-load is undone by the epoch/pending guard inside `enqueueAdd`.
    for (const id of Array.from(pendingSig.current.keys())) {
      if (!desired.has(id)) {
        pendingSig.current.delete(id)
        queue.cancel(id)
      }
    }

    // 3. Starting a run begins from a clean stage: drop the whole retained pool
    //    so nothing from an earlier scrub is one flag flip away from appearing.
    if (startedPlaying) {
      for (const id of Array.from(residentSig.current.keys())) {
        if (!visibleIds.current.has(id)) dispose(id)
      }
    }

    // 4. On-stage actors: show what's resident, add what isn't.
    for (const [id, { sig, pose, enqueue }] of desired) {
      lastDesiredSeq.current.set(id, seq.current++)
      let cur = residentSig.current.get(id)

      if (cur === sig) {
        if (visibleIds.current.has(id)) continue
        if (layerService.setSceneObjectVisible(id, true)) {
          visibleIds.current.add(id)
          // Re-entering from the pool with a stale transform — pose it now so it
          // never shows for a frame at the spot where it left the stage.
          if (pose) layerService.setAircraftTransform(id, pose.position, pose.rotationDeg)
          continue
        }
        // The object is gone (removed behind our back) — rebuild it.
        residentSig.current.delete(id)
        visibleIds.current.delete(id)
        cur = undefined
      }

      // Identity changed (different model) — the old object can't be reused.
      if (cur !== undefined) dispose(id)

      // Already queued for this identity: leave the in-flight add alone rather
      // than re-enqueueing, which could double-insert once it starts running.
      if (pendingSig.current.get(id) === sig) continue
      pendingSig.current.set(id, sig)
      enqueue()
    }

    // 5. Bound the retained pool — dispose the least-recently-desired hidden
    //    actors once it overflows.
    const hidden = Array.from(residentSig.current.keys()).filter(
      (id) => !visibleIds.current.has(id)
    )
    if (hidden.length > MAX_RETAINED_HIDDEN) {
      hidden.sort(
        (a, b) => (lastDesiredSeq.current.get(a) ?? -1) - (lastDesiredSeq.current.get(b) ?? -1)
      )
      for (const id of hidden.slice(0, hidden.length - MAX_RETAINED_HIDDEN)) dispose(id)
    }

    // 6. Orphan sweep — any of our actors in the scene that we aren't tracking.
    for (const id of layerService.getSceneObjectIds()) {
      if (!isEventActorId(id)) continue
      if (residentSig.current.has(id)) continue
      layerService.removeAircraft(id)
    }

    // Animation OFF: parked pose comes from the placement (not a motion plan), and
    // the membership reconcile above skips same-identity actors — so a live zone
    // edit would otherwise leave already-placed planes at their old spot. Reposition
    // in-scene actors whose parked pose changed (newly-added ones land fresh at
    // drain time, so they're left to the queue).
    if (!animate) {
      let moved = false
      for (const p of Object.values(placements)) {
        if (!(p.arrival <= currentTime && currentTime < p.departure)) continue
        const id = `evt:${p.eventAllocationId}`
        if (!residentSig.current.has(id)) continue
        const psig = `${p.position[0].toFixed(1)}:${p.position[1].toFixed(1)}:${Math.round(p.rotation)}`
        if (parkedSig.current.get(id) === psig) continue
        if (layerService.setAircraftTransform(id, [p.position[0], 0, p.position[1]], p.rotation)) {
          parkedSig.current.set(id, psig)
          moved = true
        }
      }
      if (moved) layerService.markSceneDirty()
    }
  }, [
    layerService,
    placements,
    reconcileTick,
    catalog,
    plans,
    motionConfig,
    fuelByScheduleId,
    playback,
    stageEpoch,
  ])

  // Clean up all event objects when the planner unmounts. Purges by id prefix,
  // not by what we tracked, so nothing of ours can outlive the page.
  useEffect(() => {
    return () => {
      queueRef.current?.clear()
      layerService?.removeSceneObjectsWhere(isEventActorId)
      residentSig.current.clear()
      visibleIds.current.clear()
      lastDesiredSeq.current.clear()
      pendingSig.current.clear()
      parkedSig.current.clear()
    }
  }, [layerService])

  return null
}
